import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { Section } from '@/components/layout/Section'
import { Container } from '@/components/layout/Container'
import { CardSlider } from '@/components/ui/CardSlider'
import { PropertyCard } from '@/components/property/PropertyCard'
import { PropertyCardSkeleton } from '@/components/property/PropertySkeleton'
import { Button } from '@/components/ui/button'
import { SECTION_PY_AFTER_HERO } from '@/lib/layoutSpacing'
import type { Property } from '@/types/property'
import { cn } from '@/lib/utils'

interface HomePropertySectionProps {
  title: string
  subtitle?: string
  viewAllHref: string
  properties: Property[]
  isLoading?: boolean
  /** First section after hero gets tighter top padding */
  first?: boolean
  /** Light tinted background band */
  tinted?: boolean
  action?: ReactNode
}

export function HomePropertySection({
  title,
  subtitle,
  viewAllHref,
  properties,
  isLoading,
  first = false,
  tinted = false,
  action,
}: HomePropertySectionProps) {
  if (!isLoading && properties.length === 0) return null

  const content = (
    <Section
      className={cn(first && SECTION_PY_AFTER_HERO, first && 'pt-5 md:pt-12')}
      title={title}
      subtitle={subtitle}
      animate={false}
      action={
        action ?? (
          <Button asChild variant="ghost" className="flex h-8 px-2 text-xs sm:h-9 sm:px-4 sm:text-sm">
            <Link to={viewAllHref}>
              View All <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </Link>
          </Button>
        )
      }
    >
      <CardSlider size="property" navVariant="side" hideScrollbar equalHeight>
        {isLoading
          ? Array.from({ length: 6 }).map((_, i) => (
              <PropertyCardSkeleton key={i} className="h-full" />
            ))
          : properties.map((p) => (
              <PropertyCard key={p.id} property={p} className="h-full" noHover />
            ))}
      </CardSlider>
    </Section>
  )

  if (tinted) {
    return (
      <div className="bg-linear-to-b from-primary-50/50 to-neutral-50">
        <Container>{content}</Container>
      </div>
    )
  }

  return <Container>{content}</Container>
}
